import { Input as AntInput, type InputProps as AntInputProps } from 'antd';
import type { TextAreaProps as AntTextAreaProps } from 'antd/es/input';
import { forwardRef } from 'react';
import './Input.css';

const { TextArea, Password, Search } = AntInput;

export interface InputProps extends Omit<AntInputProps, 'size'> {
  /**
   * Input size
   * @default 'middle'
   */
  size?: 'small' | 'middle' | 'large';
  /**
   * Error state
   */
  error?: boolean;
  /**
   * Helper text below input
   */
  helperText?: string;
}

/**
 * Modern Input Component
 *
 * Enhanced Ant Design input with modern SaaS styling
 *
 * @example
 * ```tsx
 * <Input
 *   placeholder="Enter your email"
 *   prefix={<MailOutlined />}
 *   size="large"
 * />
 * ```
 */
export const Input = forwardRef<any, InputProps>(
  ({ size = 'middle', error, helperText, className, status, ...props }, ref) => {
    const classNames = [
      'modern-input',
      error && 'modern-input--error',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <div className="modern-input-wrapper">
        <AntInput
          ref={ref}
          size={size}
          status={error ? 'error' : status}
          className={classNames}
          {...props}
        />
        {helperText && (
          <div className={`modern-input__helper ${error ? 'modern-input__helper--error' : ''}`}>
            {helperText}
          </div>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

// Export sub-components
export const InputTextArea = forwardRef<any, AntTextAreaProps>((props, ref) => (
  <TextArea ref={ref} className="modern-input modern-input--textarea" {...props} />
));

InputTextArea.displayName = 'InputTextArea';

export const InputPassword = forwardRef<any, InputProps>((props, ref) => (
  <Password ref={ref} className="modern-input" {...props} />
));

InputPassword.displayName = 'InputPassword';

export const InputSearch = forwardRef<any, InputProps>((props, ref) => (
  <Search ref={ref} className="modern-input" {...props} />
));

InputSearch.displayName = 'InputSearch';
